import type {ReactNode} from 'react'
import {memo, useRef, useState} from 'react'
import AutoSizer from 'react-virtualized/dist/es/AutoSizer'
import CellMeasurer, {CellMeasurerCache} from 'react-virtualized/dist/es/CellMeasurer'
import List from 'react-virtualized/dist/es/List'
import WindowScroller from 'react-virtualized/dist/es/WindowScroller'

import type {VirtualizerItem} from '../../Parameters.types'

import styles from './ParametersVirtualizer.css'

interface Props {
  tree: VirtualizerItem[]
  getItem: (item: VirtualizerItem) => ReactNode
}

const defaultHeight = 50

function ParametersVirtualizerComponent(props: Props) {
  const {tree, getItem} = props

  const [cache] = useState(
    new CellMeasurerCache({
      defaultHeight,
      fixedWidth: true,
    }),
  )

  const prevList = useRef(tree)

  if (prevList.current !== tree) {
    cache.clearAll?.()
    prevList.current = tree
  }

  return (
    <div className={styles.virtualizer}>
      <WindowScroller>
        {({height, scrollTop, isScrolling, onChildScroll}) => (
          <AutoSizer disableHeight>
            {size => (
              <List
                aria-label="Parameters"
                role="treegrid"
                autoHeight
                width={size.width}
                height={height}
                scrollTop={scrollTop}
                isScrolling={isScrolling}
                onScroll={onChildScroll}
                rowCount={tree.length}
                estimatedRowSize={defaultHeight}
                deferredMeasurementCache={cache}
                rowHeight={cache.rowHeight}
                rowRenderer={({index, style, parent}) => {
                  const item = tree[index]
                  return (
                    <CellMeasurer
                      cache={cache}
                      columnIndex={0}
                      key={item.key}
                      parent={parent}
                      rowIndex={index}
                    >
                      {({registerChild}) => (
                        <div
                          role="row"
                          data-parameter-row
                          data-id={item.key}
                          style={style}
                          ref={ref => registerChild?.(ref as Element)}
                        >
                          {getItem(item)}
                        </div>
                      )}
                    </CellMeasurer>
                  )
                }}
              />
            )}
          </AutoSizer>
        )}
      </WindowScroller>
    </div>
  )
}

export const ParametersVirtualizer = memo<Props>(ParametersVirtualizerComponent)
